import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Sparkles,
    ArrowRight,
    Home,
    Users,
    Zap,
    Heart,
    ShieldCheck,
    MessageCircle,
    Gift,
    Star,
    Check,
    X,
    MapPin,
    BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { FlatchWordmark } from "@/components/FlatchLogo";

const HERO_FLAT =
    "https://images.unsplash.com/photo-1665249934445-1de680641f50?crop=entropy&cs=srgb&fm=jpg&w=900&q=85";
const AVATAR_A =
    "https://images.unsplash.com/photo-1624610806209-82a4cbb4339a?crop=entropy&cs=srgb&fm=jpg&w=240&q=80";
const AVATAR_B =
    "https://images.unsplash.com/photo-1725033489648-a819750348eb?crop=entropy&cs=srgb&fm=jpg&w=240&q=80";
const AVATAR_C =
    "https://images.unsplash.com/photo-1497487231007-0a45e7b4b68a?crop=entropy&cs=srgb&fm=jpg&w=240&q=80";

const HYPE_LINES = [
    "The roommate app you didn't know you needed.",
    "Where 'good on paper' finally means 'good in real life'.",
    "Match with humans, not spreadsheets.",
    "Roommate hunting, but make it fun.",
];

const STATS = [
    { value: "8", label: "cities live" },
    { value: "60s", label: "to your vibe" },
    { value: "100%", label: "free right now" },
];

const FEATURES = [
    { icon: Sparkles, title: "60-sec Vibe Quiz", desc: "Answer a few questions. We match you by lifestyle, not just budget." },
    { icon: Heart, title: "Swipe by vibe", desc: "Every profile shows a live match %. Left to pass, right for maybe." },
    { icon: MessageCircle, title: "Chat when it clicks", desc: "Mutual matches unlock chat. Ghost-nudge dead conversations back." },
    { icon: ShieldCheck, title: "Verified & safe", desc: "Selfie verification + AI-moderated chat. Bad vibes get blocked." },
    { icon: Home, title: "Flats or roommates", desc: "Post a listing or scroll room tours. Filter by city, budget, and vibe." },
    { icon: Users, title: "Friendly contracts", desc: "Auto-generate a fun-but-legit roommate agreement PDF. Sign in-app." },
];

const CITIES = ["Mumbai", "Bengaluru", "Delhi", "Hyderabad", "Pune", "Chennai", "Kolkata", "Gurgaon"];

function HeroVisual() {
    return (
        <div className="relative w-full max-w-[380px] mx-auto">
            {/* Glow behind card */}
            <div
                className="absolute -inset-6 rounded-[44px] blur-3xl opacity-60"
                style={{ background: "radial-gradient(60% 60% at 60% 30%, hsl(280 90% 65% / 0.45), transparent 70%)" }}
            />

            {/* Main swipe card */}
            <motion.div
                initial={{ opacity: 0, y: 24, rotate: -3 }}
                animate={{ opacity: 1, y: 0, rotate: -2 }}
                transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
                className="relative rounded-[32px] overflow-hidden bg-card shadow-2xl ring-1 ring-black/5 dark:ring-white/10"
            >
                <div className="relative h-[320px]">
                    <img src={HERO_FLAT} alt="Sunlit apartment" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />

                    {/* match badge */}
                    <div className="absolute top-4 right-4 rounded-full bg-white/90 dark:bg-black/70 backdrop-blur px-3 py-1.5 flex items-center gap-1.5 shadow-lg">
                        <span className="text-sm font-black text-primary">94%</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-foreground/70">match</span>
                    </div>

                    {/* verified pill */}
                    <div className="absolute top-4 left-4 rounded-full bg-primary/90 backdrop-blur px-2.5 py-1 flex items-center gap-1 text-white shadow-lg">
                        <BadgeCheck className="h-3.5 w-3.5" strokeWidth={3} />
                        <span className="text-[10px] font-black uppercase tracking-wider">verified</span>
                    </div>

                    {/* bottom info */}
                    <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                        <div className="flex items-center gap-1.5 text-xs font-semibold opacity-90">
                            <MapPin className="h-3.5 w-3.5" /> Indiranagar, Bengaluru
                        </div>
                        <div className="mt-1 font-display font-black text-2xl leading-tight">
                            2BHK · ₹18k / mo
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {["early bird", "gym rat", "no smoking"].map((t) => (
                                <span key={t} className="rounded-full bg-white/20 backdrop-blur px-2.5 py-0.5 text-[10px] font-bold">
                                    {t}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* swipe controls */}
                <div className="flex items-center justify-center gap-4 py-4 bg-card">
                    <div className="h-11 w-11 rounded-full grid place-items-center bg-secondary text-muted-foreground shadow-sm">
                        <X className="h-5 w-5" strokeWidth={3} />
                    </div>
                    <div className="h-14 w-14 rounded-full grid place-items-center bg-primary text-primary-foreground shadow-lg shadow-primary/30">
                        <Heart className="h-6 w-6" fill="currentColor" strokeWidth={0} />
                    </div>
                    <div className="h-11 w-11 rounded-full grid place-items-center bg-secondary text-muted-foreground shadow-sm">
                        <Star className="h-5 w-5" fill="currentColor" strokeWidth={0} />
                    </div>
                </div>
            </motion.div>

            {/* Floating "It's a match" chip */}
            <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
                className="absolute -left-4 sm:-left-10 top-8 rounded-2xl bg-card shadow-xl ring-1 ring-black/5 dark:ring-white/10 px-3 py-2.5 flex items-center gap-2"
            >
                <div className="flex -space-x-2">
                    <img src={AVATAR_A} className="h-8 w-8 rounded-full object-cover ring-2 ring-card" alt="" />
                    <img src={AVATAR_B} className="h-8 w-8 rounded-full object-cover ring-2 ring-card" alt="" />
                </div>
                <div className="leading-tight">
                    <div className="text-[11px] font-black">It's a match! 🎉</div>
                    <div className="text-[10px] text-muted-foreground">Say hi to your roomie</div>
                </div>
            </motion.div>

            {/* Floating chat bubble */}
            <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
                className="absolute -right-3 sm:-right-8 bottom-16 rounded-2xl bg-primary text-primary-foreground shadow-xl px-3.5 py-2.5 max-w-[180px]"
            >
                <div className="flex items-center gap-2">
                    <img src={AVATAR_C} className="h-7 w-7 rounded-full object-cover ring-2 ring-white/30" alt="" />
                    <div className="text-[11px] font-semibold leading-snug">yo the flat looks unreal 😍 when can I see it?</div>
                </div>
            </motion.div>
        </div>
    );
}

export default function Landing() {
    const navigate = useNavigate();
    const { user, status } = useAuth();
    const [hypeIdx, setHypeIdx] = useState(0);

    useEffect(() => {
        if (status === "authed" && user) navigate("/app/match", { replace: true });
    }, [status, user, navigate]);

    useEffect(() => {
        const t = setInterval(() => setHypeIdx((i) => (i + 1) % HYPE_LINES.length), 3500);
        return () => clearInterval(t);
    }, []);

    return (
        <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
            {/* Top nav */}
            <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/60">
                <div className="mx-auto max-w-6xl px-5 h-16 flex items-center justify-between">
                    <FlatchWordmark />
                    <div className="flex items-center gap-2">
                        <Link to="/login" data-testid="landing-login-link">
                            <Button variant="ghost" className="h-10 rounded-full font-bold text-sm">
                                Log in
                            </Button>
                        </Link>
                        <Link to="/register" data-testid="landing-signup-btn">
                            <Button className="h-10 rounded-full font-bold text-sm px-5 lift shadow-lg shadow-primary/20">
                                Sign up free
                            </Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative">
                {/* ambient gradient background */}
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div
                        className="absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full blur-3xl opacity-40"
                        style={{ background: "radial-gradient(closest-side, hsl(270 90% 65% / 0.5), transparent)" }}
                    />
                    <div
                        className="absolute top-24 -right-20 h-[360px] w-[360px] rounded-full blur-3xl opacity-30"
                        style={{ background: "radial-gradient(closest-side, hsl(320 90% 60% / 0.5), transparent)" }}
                    />
                </div>

                <div className="relative mx-auto max-w-6xl px-5 pt-14 pb-16 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
                    {/* Left copy */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 text-primary px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-widest">
                            <Gift className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} /> Free for everyone until Oct 9
                        </div>

                        <h1 className="mt-5 font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-[68px] leading-[0.95]">
                            Find your <span className="font-fancy text-gradient-purple">flat.</span>
                            <br />
                            Meet your <span className="font-fancy text-gradient-purple">people.</span>
                        </h1>

                        <motion.p
                            key={hypeIdx}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-5 text-lg text-muted-foreground max-w-md leading-relaxed"
                        >
                            {HYPE_LINES[hypeIdx]}
                        </motion.p>

                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link to="/register" data-testid="landing-hero-cta">
                                <Button className="h-14 px-7 rounded-2xl font-bold text-base lift shadow-xl shadow-primary/30">
                                    <Zap className="mr-2 h-5 w-5" fill="currentColor" strokeWidth={0} /> Get started free
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </Link>
                            <Link to="/login" data-testid="landing-hero-login">
                                <Button variant="outline" className="h-14 px-7 rounded-2xl font-bold text-base border-border">
                                    I have an account
                                </Button>
                            </Link>
                        </div>

                        {/* social proof */}
                        <div className="mt-9 flex items-center gap-6">
                            <div className="flex items-center">
                                <div className="flex -space-x-2.5">
                                    {[AVATAR_A, AVATAR_B, AVATAR_C].map((a, i) => (
                                        <img key={i} src={a} className="h-9 w-9 rounded-full object-cover ring-2 ring-background" alt="" />
                                    ))}
                                </div>
                                <div className="ml-3 leading-tight">
                                    <div className="flex text-primary">
                                        {[...Array(5)].map((_, i) => (
                                            <Star key={i} className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
                                        ))}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">loved by GenZ renters</div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5 text-primary" strokeWidth={3} /> No card required</span>
                            <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5 text-primary" strokeWidth={3} /> Made in India 🇮🇳</span>
                            <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5 text-primary" strokeWidth={3} /> ₹99/mo after launch</span>
                        </div>
                    </motion.div>

                    {/* Right visual */}
                    <div className="relative">
                        <HeroVisual />
                    </div>
                </div>

                {/* Stats strip */}
                <div className="relative mx-auto max-w-6xl px-5 pb-4">
                    <div className="grid grid-cols-3 gap-3 rounded-3xl border border-border bg-card/70 backdrop-blur p-2">
                        {STATS.map((s) => (
                            <div key={s.label} className="text-center py-4">
                                <div className="font-display font-black text-3xl sm:text-4xl text-gradient-purple">{s.value}</div>
                                <div className="text-xs text-muted-foreground mt-1 font-medium">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="mx-auto max-w-6xl px-5 py-20">
                <div className="max-w-2xl">
                    <div className="text-xs font-bold uppercase tracking-widest text-primary">how it works</div>
                    <h2 className="mt-2 font-display font-black tracking-tight text-4xl sm:text-5xl leading-tight">
                        The <span className="font-fancy text-gradient-purple">whole vibe</span>
                    </h2>
                    <p className="mt-3 text-muted-foreground text-lg">Everything you need to find a place and people you actually click with.</p>
                </div>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {FEATURES.map((f, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ delay: i * 0.05, duration: 0.5 }}
                            className="group rounded-3xl border border-border bg-card p-6 shadow-sm hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
                            data-testid={`landing-feature-${i}`}
                        >
                            <div className="h-12 w-12 rounded-2xl bg-primary/10 text-primary grid place-items-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                                <f.icon className="h-6 w-6" strokeWidth={2.5} />
                            </div>
                            <div className="mt-4 font-display font-black text-xl">{f.title}</div>
                            <div className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Cities */}
            <section className="mx-auto max-w-6xl px-5 pb-20">
                <div className="rounded-[32px] border border-border bg-card p-8 sm:p-10 relative overflow-hidden">
                    <div
                        className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full blur-3xl opacity-40"
                        style={{ background: "radial-gradient(closest-side, hsl(270 90% 65% / 0.5), transparent)" }}
                    />
                    <div className="relative">
                        <div className="text-xs font-bold uppercase tracking-widest text-primary">now live</div>
                        <h3 className="mt-2 font-display font-black tracking-tight text-3xl sm:text-4xl leading-tight">
                            8 cities. <span className="text-gradient-purple">Yours next.</span>
                        </h3>
                        <div className="mt-6 flex flex-wrap gap-2.5">
                            {CITIES.map((c) => (
                                <span
                                    key={c}
                                    className="rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold flex items-center gap-1.5 hover:border-primary hover:text-primary transition-colors duration-200"
                                >
                                    <MapPin className="h-3.5 w-3.5" /> {c}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Big CTA */}
            <section className="mx-auto max-w-6xl px-5 pb-24">
                <div className="rounded-[36px] bg-primary text-primary-foreground p-8 sm:p-14 relative overflow-hidden">
                    <div
                        className="pointer-events-none absolute -top-32 -right-24 h-96 w-96 rounded-full blur-3xl opacity-30"
                        style={{ background: "radial-gradient(closest-side, white, transparent)" }}
                    />
                    <div className="pointer-events-none absolute inset-0 grain" />
                    <div className="relative max-w-2xl">
                        <div className="text-xs font-bold uppercase tracking-widest opacity-80">launch week</div>
                        <h3 className="mt-3 font-display font-black tracking-tight text-4xl sm:text-5xl leading-[1.05]">
                            Everything's <span className="font-fancy">free</span>
                            <br />
                            till October 9 🎉
                        </h3>
                        <p className="mt-4 text-lg opacity-90 max-w-lg leading-relaxed">
                            Unlimited swipes, unlimited chats, full access. No paywalls, no card required. Just sign up and vibe.
                        </p>
                        <Link to="/register" data-testid="landing-bottom-cta">
                            <Button className="mt-7 h-14 px-7 rounded-2xl font-bold text-base bg-white text-primary hover:bg-white/90 lift shadow-xl">
                                Sign up in 20 seconds <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-border/60">
                <div className="mx-auto max-w-6xl px-5 py-8 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                        <FlatchWordmark />
                        <span className="opacity-60">· Made in India 🇮🇳</span>
                    </div>
                    <div className="flex items-center gap-5">
                        <Link to="/login" className="hover:text-primary transition-colors">Log in</Link>
                        <Link to="/register" className="hover:text-primary transition-colors">Sign up</Link>
                        <a href="mailto:hello@flatch.in" className="hover:text-primary transition-colors">hello@flatch.in</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
